import { useTranslation } from "react-i18next";
import { convertTC2SC } from "./tc2sc-map";
import { TRANSLATIONS, type ExtraLocale } from "./bilingual-translations";
import type { Localized } from "@/mocks/products";

const EXTRA_LOCALES: readonly ExtraLocale[] = ["de", "fr", "es", "it", "ar", "ja"];

// SKU suffix pattern — e.g. " DT101", " SF002". Used to translate product
// names like "Forwaro Dining Table DT101" by stemming the SKU, looking up
// the "Forwaro Dining Table" stem in TRANSLATIONS, then re-appending the SKU.
const SKU_TAIL = /^(.+?)\s+([A-Z]{2,}\d+[A-Z0-9]*)$/;

const lookupExtra = (en: string, short: ExtraLocale): string | null => {
  if (!en) return null;
  const full = TRANSLATIONS[en];
  if (full?.[short]) return full[short];
  // Try stem match (for dynamic strings like "Forwaro Dining Table DT101")
  const m = en.match(SKU_TAIL);
  if (m) {
    const stem = TRANSLATIONS[m[1]];
    if (stem?.[short]) return `${stem[short]} ${m[2]}`;
  }
  return null;
};

// Resolve a Localized record to the current UI locale.
//  - en              → English source
//  - zh-Hant         → zh-Hant source (or runtime fallback)
//  - zh-Hans / zh-CN → zh-Hans source, or runtime TC→SC conversion
//  - de / fr / es / it / ar / ja → TRANSLATIONS map (falls back to English)
export const resolveLocalized = (value: Localized, lang: string): string => {
  if (!value) return "";
  if (lang === "zh-Hans" || lang === "zh-CN") {
    if (value["zh-Hans"]) return value["zh-Hans"];
    if (value["zh-Hant"]) return convertTC2SC(value["zh-Hant"]);
    return value.en;
  }
  if (lang && lang.startsWith("zh")) return value["zh-Hant"] || value.en;

  // Non-Chinese, non-English: consult the shared translation map.
  const short = (lang || "").slice(0, 2).toLowerCase() as ExtraLocale;
  if ((EXTRA_LOCALES as readonly string[]).includes(short)) {
    const translated = lookupExtra(value.en, short);
    if (translated) return translated;
  }
  return value.en;
};

export const useLocalized = () => {
  const { i18n } = useTranslation();
  return (value: Localized) => resolveLocalized(value, i18n.language);
};
